import { Collection, Model } from 'backbone';
import { getLogger } from '../logging/logger';
import fetchData from '../util/fetch';
import { addReadyPromise } from '../util/utilities';

/** @enum {typeof ATTRIBUTES[keyof typeof ATTRIBUTES]} */
export const ATTRIBUTES = Object.freeze({
	type: 'type',
	contentUrl: 'contentUrl',
});

/** @enum {typeof EVENTS[keyof typeof EVENTS]} */
export const EVENTS = Object.freeze({
	initialized: 'initialized',
});

/**
 * @typedef {object} BaseDataOptions
 * @property {string} [contentUrl] - The URL to fetch data from
 * @property {string} [type] - The type of the entity the data represents
 */

/**
 * @template {BaseDataOptions} DataOptions
 * @extends Model<DataOptions>
 */
class BaseData extends Model {
	/**
	 * @returns {string} Identifier for the class name
	 */
	get CN() {
		return BaseData.name;
	}

	/**
	 * Returns a logger instance scoped to the current class.
	 */
	get logger() {
		return getLogger(this.CN);
	}

	/**
	 * Default values for this model
	 * @returns {Partial<DataOptions>}
	 */
	defaults() {
		return {
			type: 'baseData',
			contentUrl: '',
		};
	}

	/**
	 * @param {DataOptions} [options]
	 */
	constructor(options) {
		super(options);

		const { isReady, onReadyReject, onReadyResolve } = addReadyPromise();

		/** @type {Promise<any>} */
		this.isReady = isReady;
		/** @type {Function} */
		this.onReadyResolve = onReadyResolve;
		/** @type {Function} */
		this.onReadyReject = onReadyReject;
	}

	/**
	 * @param {DataOptions} [options]
	 * @returns {Promise<this>} Resolves when loading of data is complete or an error occured
	 */
	initialize(options) {
		this.logger.log(`[${this.initialize.name}]<${BaseData.name}>`, { ...options });

		return fetchData(this.get(ATTRIBUTES.contentUrl))
			.then(this.onDataFetched.bind(this))
			.then(() => {
				this.trigger(EVENTS.initialized);
				this.onReadyResolve(this);
				return this;
			})
			.catch((error) => {
				this.logger.error(`[${this.get('type')} initialize error]`, { error });
				this.onReadyReject(error);
				return error;
			});
	}

	/**
	 * @param {object} data Raw data returned from contentUrl.
	 */
	onDataFetched(data) {
		this.logger.log(`[${this.onDataFetched.name}]<${BaseData.name}>`, { ...data });

		// Create collections for each property that is a Array type. Remove array from data.
		Object.keys(data).forEach((/** @type {string} */ key) => {
			const value = data[key];
			if (Array.isArray(value)) {
				this[key] ??= new Collection();
				this[key].reset(value);
				delete data[key];
			}
		});

		// Populate model with remaining data
		this.set(data);
	}

	destroy() {
		this.logger.log(`[${this.destroy.name}]`);

		Object.keys(this).forEach((key) => {
			const value = this[key];
			if (value instanceof Collection) {
				value.toArray().forEach((model) => model.stopListening());
				value.reset();
			}
		});
	}
}

export default BaseData;
